import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, AlertCircle, Check } from 'lucide-react';
import { useForecastV3 } from '../../context/ForecastV3Context';
import { LY_BUDGET_DRIVER_BASES } from '../../types/driverTerms';
import { getVersionDriverEntry } from './driverCatalog';
import { DRIVER_CONFIG } from '../../types';
import type { DriverType, ForecastAccount, BulkDriverResponse } from '../../types';
import { getRevenueDepartments, type RevenueDepartment } from '../../api';

interface BulkDriverEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAccounts: ForecastAccount[];
  onApply: (driverType: string, driverInput: number | null, selectedDepartments?: string[]) => Promise<BulkDriverResponse>;
  isLoading?: boolean;
  orgId: number;
  locationId: number;
}

// Driver types that require input
const DRIVERS_WITH_INPUT = [
  '$ / ORN',
  '$ / ARN',
  '% of Revenue',
  '% of Selected Revenue',
];

// Drivers that require department selection
const DRIVERS_WITH_DEPARTMENT_SELECTION = [
  '% of Selected Revenue',
  '% of Selected Revenue (Budgeted)',
];

// Get input label for driver type
function getInputLabel(driverType: string): string {
  if (driverType.includes('%')) {
    return 'Percentage (%)';
  }
  if (driverType.includes('$')) {
    return 'Amount ($)';
  }
  return 'Input Value';
}

export function BulkDriverEditModal({
  isOpen,
  onClose,
  selectedAccounts,
  onApply,
  isLoading = false,
  orgId,
  locationId,
}: BulkDriverEditModalProps) {
  const { state: { currentVersion } } = useForecastV3();
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [driverInput, setDriverInput] = useState<string>('');
  const [result, setResult] = useState<BulkDriverResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Department selection state for "% of Selected Revenue"
  const [revenueDepartments, setRevenueDepartments] = useState<RevenueDepartment[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const requiresInput = DRIVERS_WITH_INPUT.includes(LY_BUDGET_DRIVER_BASES[selectedDriver] || selectedDriver);
  const requiresDepartmentSelection = DRIVERS_WITH_DEPARTMENT_SELECTION.includes(LY_BUDGET_DRIVER_BASES[selectedDriver] || selectedDriver);

  // Fetch revenue departments when modal opens or when "% of Selected Revenue" is selected
  useEffect(() => {
    if (isOpen && requiresDepartmentSelection && revenueDepartments.length === 0) {
      setLoadingDepartments(true);
      getRevenueDepartments({ orgId, locationId })
        .then(response => {
          setRevenueDepartments(response.departments);
        })
        .catch(err => {
          console.error('Failed to fetch revenue departments:', err);
        })
        .finally(() => {
          setLoadingDepartments(false);
        });
    }
  }, [isOpen, requiresDepartmentSelection, orgId, locationId, revenueDepartments.length]);

  // Toggle department selection
  const toggleDepartment = (departmentName: string) => {
    setSelectedDepartments(prev =>
      prev.includes(departmentName)
        ? prev.filter(d => d !== departmentName)
        : [...prev, departmentName]
    );
  };

  // Select/deselect all departments
  const toggleAllDepartments = () => {
    if (selectedDepartments.length === revenueDepartments.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(revenueDepartments.map(d => d.name));
    }
  };

  const handleApply = async () => {
    if (!selectedDriver) return;

    const inputValue = requiresInput && driverInput
      ? parseFloat(driverInput)
      : null;

    // Pass selected departments for "% of Selected Revenue" driver
    // Empty array means "all departments" (total revenue)
    const departments = requiresDepartmentSelection ? selectedDepartments : undefined;

    try {
      setError(null);
      const response = await onApply(selectedDriver, inputValue, departments);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update drivers');
    }
  };

  const handleClose = () => {
    setSelectedDriver('');
    setDriverInput('');
    setSelectedDepartments([]);
    setResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  // Determine effective account type from the selection
  const accountTypes = selectedAccounts.map(a => a.accountType);
  const firstType = accountTypes[0];
  const effectiveAccountType: 'Revenue' | 'Expense' | 'mixed' =
    !firstType ? 'mixed'
    : accountTypes.every(t => t === firstType) ? firstType : 'mixed';

  // Group drivers by category for display, filtered by account type
  const allDriverCategories = [
    {
      name: 'Manual / Static',
      drivers: ['Manual', 'Static Entry'],
    },
    {
      name: 'Budget',
      drivers: ['Budget', 'Budgeted $POR', 'Budgeted $PAR'],
    },
    {
      name: 'Per Room ($POR / $PAR)',
      drivers: ['$ / ORN', '$ / ARN'],
    },
    {
      name: 'Last Month',
      drivers: ['$ LM', '$ / ORN LM', '$ / ARN LM'],
    },
    {
      name: '% of Revenue',
      drivers: ['% of Revenue', '% of Selected Revenue', '% of Total Revenue (Budgeted)', '% of Selected Revenue (Budgeted)'],
    },
    {
      name: 'Last Year',
      drivers: ['LY Same Month', '$ / ORN LY', '$ / ARN LY'],
    },
    {
      name: 'Labor',
      drivers: ['$POR Projection'],
    },
  ];

  const versionCategories = allDriverCategories.map(category => ({ ...category,
    drivers: category.drivers.map(id => currentVersion?.type === 'Budget'
      ? Object.keys(LY_BUDGET_DRIVER_BASES).find(key => LY_BUDGET_DRIVER_BASES[key] === id) || id : id),
  }));
  const driverCategories = effectiveAccountType === 'mixed'
    ? versionCategories
    : versionCategories
        .map(cat => ({
          ...cat,
          drivers: cat.drivers.filter(driver => {
            const applicable = DRIVER_CONFIG[driver]?.applicableTo ?? 'both';
            return applicable === 'both' || applicable === effectiveAccountType.toLowerCase();
          }),
        }))
        .filter(cat => cat.drivers.length > 0);

  return (
    <div className="fv3-scrim fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="fv3-dialog bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="fv3-dialog-head px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="fv3-dialog-title text-lg font-semibold text-slate-900">
            Edit Drivers ({selectedAccounts.length} accounts)
          </h2>
          <button
            onClick={handleClose}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {result ? (
            // Show results
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${result.failed > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.failed > 0 ? (
                    <AlertCircle size={20} className="text-amber-600" />
                  ) : (
                    <CheckCircle size={20} className="text-emerald-600" />
                  )}
                  <span className={`font-semibold ${result.failed > 0 ? 'text-amber-800' : 'text-emerald-800'}`}>
                    {result.failed > 0 ? 'Partially Complete' : 'Success'}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  Updated {result.updated} of {selectedAccounts.length} accounts to use {selectedDriver}.
                </p>
                {result.failed > 0 && (
                  <p className="text-sm text-amber-700 mt-1">
                    {result.failed} account(s) failed to update.
                  </p>
                )}
              </div>

              <button
                onClick={handleClose}
                className="w-full py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            // Show form
            <div className="space-y-6">
              {/* Selected accounts summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Selected Accounts</h3>
                <div className="max-h-32 overflow-y-auto text-sm text-slate-600">
                  {selectedAccounts.slice(0, 5).map(account => (
                    <div key={account.chartOfAccountId} className="py-1">
                      {account.accountName}
                      <span className="text-slate-400 ml-2">({account.accountNumber})</span>
                    </div>
                  ))}
                  {selectedAccounts.length > 5 && (
                    <div className="py-1 text-slate-400">
                      ...and {selectedAccounts.length - 5} more
                    </div>
                  )}
                </div>
              </div>

              {/* Driver selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Driver Type
                </label>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {driverCategories.map(category => (
                    <div key={category.name}>
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                        {category.name}
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {category.drivers.map(driver => {
                          const config = DRIVER_CONFIG[driver];
                          const isSelected = selectedDriver === driver;
                          return (
                            <button
                              key={driver}
                              onClick={() => setSelectedDriver(driver)}
                              className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                isSelected
                                  ? ''
                                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                              }`}
                              style={isSelected ? {
                                background: 'var(--lodge-orange-100)',
                                color: 'var(--lodge-orange-800, #92400e)',
                                boxShadow: '0 0 0 2px var(--lodge-orange-500)',
                              } : undefined}
                            >
                              {getVersionDriverEntry(driver, currentVersion?.type === 'Budget', currentVersion?.budgetReferenceYear)?.label || config?.label || driver}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Driver input (if required) */}
              {requiresInput && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {getInputLabel(selectedDriver)}
                  </label>
                  <input
                    type="number"
                    value={driverInput}
                    onChange={(e) => setDriverInput(e.target.value)}
                    placeholder={selectedDriver.includes('%') ? 'e.g., 10 for 10%' : 'e.g., 25.00'}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none"
                    onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px var(--lodge-orange-400)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
                    onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
                  />
                </div>
              )}

              {/* Department selection for "% of Selected Revenue" */}
              {requiresDepartmentSelection && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Select Revenue Departments
                    </label>
                    <button
                      type="button"
                      onClick={toggleAllDepartments}
                      style={{ color: 'var(--lodge-orange-500)' }}
                    >
                      {selectedDepartments.length === revenueDepartments.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    {selectedDepartments.length === 0
                      ? 'No departments selected = uses total revenue from all departments'
                      : `${selectedDepartments.length} department(s) selected`}
                  </p>
                  {loadingDepartments ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 size={20} className="animate-spin text-slate-400" />
                      <span className="ml-2 text-sm text-slate-500">Loading departments...</span>
                    </div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                      {revenueDepartments.map(dept => (
                        <label
                          key={dept.name}
                          className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                        >
                          <div
                            className={`w-5 h-5 rounded border mr-3 flex items-center justify-center transition-colors ${
                              selectedDepartments.includes(dept.name)
                                ? ''
                                : 'border-slate-300'
                            }`}
                          style={selectedDepartments.includes(dept.name) ? {
                            background: 'var(--lodge-orange-400)',
                            borderColor: 'var(--lodge-orange-400)',
                          } : undefined}
                          >
                            {selectedDepartments.includes(dept.name) && (
                              <Check size={14} className="text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedDepartments.includes(dept.name)}
                            onChange={() => toggleDepartment(dept.name)}
                            className="sr-only"
                          />
                          <span className="flex-1 text-sm text-slate-700">{dept.name}</span>
                          <span className="text-xs text-slate-400">{dept.accountCount} accounts</span>
                        </label>
                      ))}
                      {revenueDepartments.length === 0 && (
                        <div className="px-3 py-4 text-sm text-slate-500 text-center">
                          No revenue departments found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={!selectedDriver || isLoading || (requiresInput && !driverInput)}
                  className="flex-1 py-2.5 rounded-lg font-medium disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', boxShadow: 'var(--shadow-orange)' }}
                  onMouseEnter={e => { if (!(e.currentTarget as HTMLButtonElement).disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
                  onMouseLeave={e => { if (!(e.currentTarget as HTMLButtonElement).disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Applying...
                    </>
                  ) : (
                    'Apply to All'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkDriverEditModal;
